import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { LayoutGroup } from '../models/layout.model';
import { LayoutService } from '../services/layout.service';
import { ProvisioningService } from '../../provisioned-devices/services/provisioning-service/provisioning-service';
import { DeviceProvisioningResponse } from '../../provisioned-devices/models/device-provisioning-response.model';
import { ToastService } from '../../../shared/services/toast-service';
import { Modal } from '../../../shared/components/modal/modal';
import { ModalFooterDirective } from '../../../shared/components/modal/modal-footer.directive';

@Component({
  selector: 'app-assign-device-dialog',
  standalone: true,
  imports: [Modal, ModalFooterDirective],
  templateUrl: './assign-device-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignDeviceDialogComponent implements OnInit {
  private readonly layoutService = inject(LayoutService);
  private readonly provisioningService = inject(ProvisioningService);
  private readonly toastService = inject(ToastService);
  private readonly destroy$ = new Subject<void>();

  @Input() group!: LayoutGroup;
  @Output() close = new EventEmitter<void>();

  readonly open = signal(true);
  devices = signal<DeviceProvisioningResponse[]>([]);
  selectedDevice = signal<DeviceProvisioningResponse | null>(null);
  isLoading = signal(false);

  ngOnInit(): void {
    this.provisioningService
      .getProvisionedDevices(0, 100, 'clientId', 'ASC')
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => this.devices.set(res.data));
  }

  selectDevice(device: DeviceProvisioningResponse): void {
    this.selectedDevice.set(device);
  }

  assign(): void {
    const device = this.selectedDevice();
    if (!device) return;
    this.isLoading.set(true);
    this.layoutService
      .assignLayoutGroupToDevice(device.clientId, this.group.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show(`Layout group assigned to ${device.clientId}`, 'success');
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show('Failed to assign layout group', 'error');
          this.isLoading.set(false);
        },
      });
  }

  sendAllTemplates(): void {
    const device = this.selectedDevice();
    if (!device) return;
    this.isLoading.set(true);
    this.layoutService
      .sendAllTemplatesToDevice(device.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show('All templates sent via MQTT', 'success');
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show('Failed to send templates', 'error');
          this.isLoading.set(false);
        },
      });
  }

  sendAllTemplateGroups(): void {
    const device = this.selectedDevice();
    if (!device) return;
    this.isLoading.set(true);
    this.layoutService
      .sendAllTemplateGroupsToDevice(device.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show('All template groups sent via MQTT', 'success');
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show('Failed to send template groups', 'error');
          this.isLoading.set(false);
        },
      });
  }

  onClose(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.close.emit();
  }
}
